<div class="space-y-6">
  <app-page-header
    title="Contrôle d'accès"
    subtitle="Permissions, resources et scopes Keycloak qui pilotent les accès fins de la plateforme."
  >
    <div slot="actions" class="flex flex-wrap gap-2">
      <button
        type="button"
        (click)="openBulk()"
        class="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5"
      >
        Lier un rôle
      </button>
      <button
        type="button"
        (click)="openSeed()"
        class="inline-flex items-center justify-center gap-2 rounded-lg border border-brand-300 px-5 py-2.5 text-sm font-medium text-brand-700 transition hover:bg-brand-50 dark:border-brand-800 dark:text-brand-400 dark:hover:bg-brand-500/10"
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
          <path d="M3.333 10c0-3.682 2.985-6.667 6.667-6.667 1.84 0 3.504.745 4.71 1.949L12.5 7.5h5v-5l-2.018 2.018A8.305 8.305 0 0 0 10 1.667C5.398 1.667 1.667 5.398 1.667 10S5.398 18.333 10 18.333a8.34 8.34 0 0 0 8.082-6.25h-1.72A6.671 6.671 0 0 1 10 16.667c-3.682 0-6.667-2.985-6.667-6.667Z" fill="currentColor"/>
        </svg>
        Seeder
      </button>
      <a
        routerLink="/permissions/new"
        class="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white shadow-theme-xs transition hover:bg-brand-600"
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
          <path d="M10 4.167v11.666M4.167 10h11.666" stroke="currentColor" stroke-width="1.667" stroke-linecap="round" />
        </svg>
        Nouvelle permission
      </a>
    </div>
  </app-page-header>

  <app-permissions-tabs />

  <section class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
    <div class="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
      <div class="relative max-w-md">
        <span class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M3.04199 9.37381C3.04199 5.87712 5.87711 3.04199 9.3738 3.04199C12.8705 3.04199 15.7056 5.87712 15.7056 9.37381C15.7056 10.9354 15.1399 12.3646 14.2024 13.4685L16.5979 15.8638C16.8908 16.1567 16.8908 16.6315 16.5979 16.9244C16.305 17.2173 15.8302 17.2173 15.5373 16.9244L13.1422 14.529C12.0381 15.4674 10.6083 16.0337 9.3738 16.0337C5.87711 16.0337 3.04199 13.1985 3.04199 9.37381ZM9.3738 4.54199C6.70557 4.54199 4.54199 6.70557 4.54199 9.37381C4.54199 12.0421 6.70557 14.2056 9.3738 14.2056C12.042 14.2056 14.2056 12.0421 14.2056 9.37381C14.2056 6.70557 12.042 4.54199 9.3738 4.54199Z" fill="currentColor"/>
          </svg>
        </span>
        <input
          type="text"
          placeholder="Rechercher (nom, type, libellé…)"
          [formControl]="searchCtrl"
          class="h-11 w-full rounded-lg border border-gray-300 bg-transparent pl-11 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
        />
      </div>
    </div>

    @if (error() || loading() || filtered().length === 0) {
      <app-data-state
        [loading]="loading()"
        [error]="error()"
        [empty]="!loading() && !error() && filtered().length === 0"
        loadingMessage="Chargement des permissions…"
        emptyMessage="Aucune permission. Lance le seeder pour créer le set initial."
        (retry)="load()"
      />
    } @else {
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead class="bg-gray-50 dark:bg-white/[0.02]">
            <tr>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Permission</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Type</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Scopes</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Stratégie</th>
              <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
            @for (p of filtered(); track p.id) {
              <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                <td class="px-5 py-4">
                  <p class="font-mono text-sm font-medium text-gray-800 dark:text-white/90">{{ p.name }}</p>
                  @if (p.displayName && p.displayName !== p.name) {
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ p.displayName }}</p>
                  }
                </td>
                <td class="whitespace-nowrap px-5 py-4">
                  <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                    {{ p.type || '—' }}
                  </span>
                </td>
                <td class="px-5 py-4">
                  @if (p._scopesPreview.length) {
                    <div class="flex flex-wrap gap-1">
                      @for (s of p._scopesPreview; track s) {
                        <span class="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-400">{{ s }}</span>
                      }
                      @if (p._scopesOverflow > 0) {
                        <span class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">+{{ p._scopesOverflow }}</span>
                      }
                    </div>
                  } @else {
                    <span class="text-xs text-gray-400">—</span>
                  }
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-xs text-gray-500 dark:text-gray-400">
                  {{ p.decisionStrategy || '—' }}
                </td>
                <td class="whitespace-nowrap px-5 py-4 text-right">
                  <a [routerLink]="['/permissions', p.id]" class="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400">Voir</a>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="border-t border-gray-200 px-5 py-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {{ filtered().length }} permission(s)
      </div>
    }
  </section>
</div>

<!-- Bulk role assignment modal -->
<app-modal [isOpen]="bulkOpen()" (close)="closeBulk()" className="w-full max-w-lg rounded-2xl bg-white p-6 dark:bg-gray-dark">
  @if (!bulkResult()) {
    <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Lier un rôle à plusieurs permissions</h3>
    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
      Ajoute (ou retire) la <em>policy</em> du rôle aux permissions sélectionnées en une seule opération.
    </p>

    <form [formGroup]="bulkForm" class="mt-4 space-y-4">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Rôle <span class="text-error-500">*</span></label>
          <app-pretty-select
            formControlName="roleName"
            [options]="roleOptions()"
            placeholder="— choisir —"
            searchable
          />
        </div>
        <div>
          <label class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Opération</label>
          <app-pretty-select formControlName="operation" [options]="bulkOpOptions" />
        </div>
      </div>

      <div>
        <input type="text" formControlName="filter" placeholder="Filtrer les permissions…" class="h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800" />
      </div>

      <div class="max-h-[40vh] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800">
        @if (filteredBulkPerms().length === 0) {
          <p class="px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">Aucune permission ne correspond.</p>
        } @else {
          <ul class="divide-y divide-gray-200 dark:divide-gray-800">
            @for (p of filteredBulkPerms(); track p.id) {
              <li>
                <label class="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                  <input type="checkbox" [checked]="isPermSelected(p.id)" (change)="togglePerm(p.id)" [disabled]="!p.id" class="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/20 dark:border-gray-600" />
                  <span class="min-w-0 flex-1">
                    <span class="font-mono text-xs text-gray-800 dark:text-white/90">{{ p.name }}</span>
                    @if (p.displayName && p.displayName !== p.name) {
                      <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">{{ p.displayName }}</span>
                    }
                  </span>
                </label>
              </li>
            }
          </ul>
        }
      </div>

      @if (bulkError(); as msg) {
        <div class="rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
      }

      <div class="flex items-center justify-between pt-2">
        <span class="text-xs text-gray-500 dark:text-gray-400">{{ selectedPermissionIds().size }} sélectionnée(s)</span>
        <div class="flex gap-2">
          <button type="button" (click)="closeBulk()" [disabled]="bulkLoading()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
          <button type="button" (click)="submitBulk()" [disabled]="bulkLoading() || bulkForm.invalid || selectedPermissionIds().size === 0" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300">
            @if (bulkLoading()) {
              <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
                <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
              </svg>
              En cours…
            } @else { Appliquer }
          </button>
        </div>
      </div>
    </form>
  } @else {
    <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Opération terminée</h3>
    <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">{{ bulkResult() }}</p>
    <div class="mt-6 flex justify-end">
      <button type="button" (click)="closeBulk()" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">Fermer</button>
    </div>
  }
</app-modal>

<app-modal [isOpen]="seedOpen()" (close)="closeSeed()" className="w-full max-w-lg rounded-2xl bg-white p-6 dark:bg-gray-dark">
  @if (!seedJob()) {
    <!-- État initial : invitation à lancer le job -->
    <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Lancer le seeder</h3>
    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      Crée scopes, resources, rôles, permissions et lie les policies de rôles dans Keycloak. <strong>Idempotent</strong> — ce qui existe déjà est ignoré.
    </p>
    <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
      Exécution en arrière-plan — un suivi temps réel s'affichera ci-dessous.
    </p>
    @if (seedError(); as msg) {
      <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
        {{ msg }}
      </div>
    }
    <div class="mt-6 flex justify-end gap-2">
      <button type="button" (click)="closeSeed()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">
        Annuler
      </button>
      <button type="button" (click)="runSeed()" [disabled]="seedLoading()" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
        @if (seedLoading()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
          </svg>
          Démarrage…
        } @else { Lancer le seeder }
      </button>
    </div>
  } @else {
    <!-- Job actif ou terminé : header dynamique + progression -->
    <div class="flex items-start justify-between gap-3">
      <div>
        <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">
          @if (seedJob()?.status === 'PENDING') { Seeder — en attente }
          @else if (seedJob()?.status === 'RUNNING') { Seeder — en cours }
          @else if (seedJob()?.status === 'SUCCEEDED') { Seeder terminé }
          @else if (seedJob()?.status === 'FAILED') { Seeder échoué }
        </h3>
        <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Job <span class="font-mono">{{ seedJob()?.jobId }}</span>
          @if (seedRunning()) { · étape : <span class="font-medium text-gray-700 dark:text-gray-300">{{ seedCurrentStepLabel() }}</span> }
        </p>
      </div>
      <span
        class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
        [class.bg-gray-100]="seedJob()?.status === 'PENDING'"
        [class.text-gray-700]="seedJob()?.status === 'PENDING'"
        [class.dark:bg-gray-800]="seedJob()?.status === 'PENDING'"
        [class.dark:text-gray-300]="seedJob()?.status === 'PENDING'"
        [class.bg-brand-50]="seedJob()?.status === 'RUNNING'"
        [class.text-brand-700]="seedJob()?.status === 'RUNNING'"
        [class.dark:bg-brand-500/15]="seedJob()?.status === 'RUNNING'"
        [class.dark:text-brand-400]="seedJob()?.status === 'RUNNING'"
        [class.bg-success-50]="seedJob()?.status === 'SUCCEEDED'"
        [class.text-success-700]="seedJob()?.status === 'SUCCEEDED'"
        [class.dark:bg-success-500/15]="seedJob()?.status === 'SUCCEEDED'"
        [class.dark:text-success-400]="seedJob()?.status === 'SUCCEEDED'"
        [class.bg-error-50]="seedJob()?.status === 'FAILED'"
        [class.text-error-700]="seedJob()?.status === 'FAILED'"
        [class.dark:bg-error-500/10]="seedJob()?.status === 'FAILED'"
        [class.dark:text-error-400]="seedJob()?.status === 'FAILED'"
      >{{ seedJob()?.status }}</span>
    </div>

    <!-- Progression globale -->
    <div class="mt-4">
      <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>Progression globale</span>
        <span class="font-medium text-gray-700 dark:text-gray-300">{{ seedProgressPercent() }}%</span>
      </div>
      <div class="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div
          class="h-full rounded-full transition-all duration-300"
          [class.bg-brand-500]="seedJob()?.status !== 'FAILED' && seedJob()?.status !== 'SUCCEEDED'"
          [class.bg-success-500]="seedJob()?.status === 'SUCCEEDED'"
          [class.bg-error-500]="seedJob()?.status === 'FAILED'"
          [style.width.%]="seedProgressPercent()"
        ></div>
      </div>
    </div>

    <!-- Progression par étape -->
    <ul class="mt-5 space-y-3">
      @for (step of seedSteps(); track step.key) {
        <li>
          <div class="flex items-center justify-between text-xs">
            <span class="font-medium text-gray-700 dark:text-gray-300">{{ step.label }}</span>
            <span class="text-gray-500 dark:text-gray-400">{{ step.done }} / {{ step.total }}</span>
          </div>
          <div class="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <div
              class="h-full bg-brand-400 transition-all duration-300"
              [style.width.%]="step.total > 0 ? (step.done * 100 / step.total) : 0"
            ></div>
          </div>
        </li>
      }
    </ul>

    @if (seedJob()?.status === 'FAILED') {
      <div class="mt-4 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
        {{ seedJob()?.errorMessage || 'Le seeder a échoué — voir les logs back.' }}
      </div>
    }
    @if (seedJob()?.status === 'SUCCEEDED' && seedJob()?.result?.errors?.length) {
      <div class="mt-4 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:border-warning-800 dark:bg-warning-500/10 dark:text-warning-400">
        {{ seedJob()?.result?.errors?.length }} avertissement(s) — voir la console.
      </div>
    }
    @if (seedError(); as msg) {
      <div class="mt-3 rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">
        {{ msg }}
      </div>
    }

    <div class="mt-6 flex justify-end gap-2">
      <button type="button" (click)="closeSeed()" [disabled]="seedRunning()" class="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300">
        @if (seedRunning()) { Veuillez patienter… } @else { Fermer }
      </button>
    </div>
  }
</app-modal>
